import { useTranslation } from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import DelimiterInput from '../DelimiterInput'
import { useGetEDIVersionsQuery, useGetTransSetsQuery } from '../../../redux/api';
import { useState, useEffect, useContext, useRef } from 'react';
import {each} from "lodash";
import {treeActions} from "../../../redux/slices/treeSlice";
import GroupByName from "../GroupByName";
import TPCheckbox from '../TPCheckbox';
import { Button, FormControl, InputLabel, MenuItem, Select, Stack, Tooltip, styled, tooltipClasses } from "@mui/material";
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { ParentScreenCtx } from "/src/pages/workbench_build/WorkbenchBuild";
import OverrideInboundDelimeter from "../OverrideInboundDelimeter";
import { getRePopulatedData } from "../../../utils/rcTreePopulate";
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';

function showGlobalErrorToast(message) {
  window.dispatchEvent(new CustomEvent('showToast', {
    detail: { message, severity: 'error' }
  }));
}

function EDIForm() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { account } = useSelector(state => state.queryParams);
  const parentScreen = useContext(ParentScreenCtx);
  const { rightTree } = useSelector(state => state.treeData[parentScreen]);
  const [version , setVersion] = useState('');
  const [versionLoading, setVersionLoading] = useState(true);
  const [versionOptions, setVersionOptions] = useState([]);
  const [versionWarningIcon, setVersionWarningIcon] = useState(<ReportProblemRoundedIcon sx={{fontSize: '26px', color: '#c4c4c4'}}/>);
  const [transaction , setTransaction] = useState('');
  const [transactionLoading, setTransactionLoading] = useState(true);
  const [transactionOptions, setTransactionOptions] = useState([]);
  const [transactionWarningIcon, setTransactionWarningIcon] = useState(<ReportProblemRoundedIcon sx={{fontSize: '26px', color: '#c4c4c4'}}/>);
  const [recordDelimiter, setRecordDelimiter] = useState(false);
  const [fieldDelimiter, setFieldDelimiter] = useState(false);
  const [componentSeparator, setComponentSeparator] = useState(false);
  const [repetitionSeparator, setRepetitionSeparator] = useState(false);
  const [stripQuotes, setStripQuotes] = useState(false);
  const [trimData, setTrimData] = useState(false);
  const [decodeData, setDecodeData] = useState(false);
  const ediVersionsRes = useGetEDIVersionsQuery({ data_type: 'EDI', account: account });
  const transSetsRes = useGetTransSetsQuery({ data_type: 'EDI', account: account, data_type_version: version })

  const ediVersionsErrorToastRequestId = useRef();
  const ediVersionsInvalidToastRequestId = useRef();
  const ediTransSetsErrorToastRequestId = useRef();
  const ediTransSetsInvalidToastRequestId = useRef();

  const handleChange = (key, value) => {
    dispatch(treeActions.setRightTreeTemplateHeaders({ screen: parentScreen, key, value }));
    if (parentScreen !== "inbound") return;
    if(key === "FILETYPE_VERSION"){
      console.log(key,value);
      const rePopulatedData  = getRePopulatedData("EDI","EDI",value,rightTree?.tree,parentScreen);
      Object.keys(rePopulatedData)?.forEach(tab =>{
        dispatch(treeActions.setNewRightTreeTranslatedData({screen: parentScreen,type:tab,data:rePopulatedData?.[tab] || []}));
      })
    }
  }

  const getDataTypeVersions = () => {
    const main = [];
    const tail = [];
    if (!ediVersionsRes.isSuccess || !ediVersionsRes.data?.DATA_TYPE_VERSIONS) return [];
    each(ediVersionsRes.data.DATA_TYPE_VERSIONS, (arr) => {
      each(arr, (ver) => {
        if (/[a-zA-Z]/.test(ver)) tail.push({ key: ver, value: ver, text: ver, alpha: parseInt(ver.substring(0, 4), 10) })
        else main.push({ key: ver, value: ver, text: ver })
      })
    })
    return main.concat(tail.sort((a, b) => a.alpha - b.alpha))
  }
 
  const getTransSets = () => {
    const transSets = [];
    if (!transSetsRes.isSuccess || !Array.isArray(transSetsRes.data?.TRANS_SETS)) return transSets;
    transSetsRes.data.TRANS_SETS.forEach((ts) => {
      transSets.push({ key: ts.templateId, value: ts.templateId, text: ts.templateId })
    })
    return transSets;
  }

  useEffect(() => {
    const header = rightTree.tree.templateHeader;
    if (version !== header.FILETYPE_VERSION) setVersion(header.FILETYPE_VERSION);
    setRecordDelimiter(header.LINEDELIMITER || '');
    setFieldDelimiter(header.DELIMITER || '');
    setComponentSeparator(header.GROUP_SEPARATOR || '');
    setRepetitionSeparator(header.REPETITION_DELIMITER || '');

    if(parentScreen === 'outbound' && !header?.REPETITION_DELIMITER){
      dispatch(treeActions.setRightTreeTemplateHeaders({screen: parentScreen, key: 'REPETITION_DELIMITER', value: '%5E'}));
      setRepetitionSeparator('%5E');
    }

    setStripQuotes(
      header.UNCSV ?
        (typeof header.UNCSV === 'boolean' && header.UNCSV) || header.UNCSV === "TRUE" || false
        : false
    );
    setTrimData(
      header.TRIMDATA ?
        (typeof header.TRIMDATA === 'boolean' && header.TRIMDATA) || header.TRIMDATA === "TRUE" || false
        : false
    );
    setDecodeData(
      header.URLDECODE ?
        (typeof header.URLDECODE === 'boolean' && header.URLDECODE) || header.URLDECODE === "TRUE" || false
        : false
    );
  }, [rightTree.tree.templateHeader])

  useEffect(() => {
    if (ediVersionsRes.isError) {
      if (ediVersionsRes.requestId !== ediVersionsErrorToastRequestId.current) {
        ediVersionsErrorToastRequestId.current = ediVersionsRes.requestId;
        showGlobalErrorToast(t('MSG_FETCH_DATA_TYPES_FAILED', {
          defaultValue: 'Something went wrong while fetching data types.'
        }));
      }
      setVersionLoading(false);
      return;
    }

    if (!ediVersionsRes.isSuccess) return;

    const versionsList = getDataTypeVersions();
    if (!versionsList.length) {
      if (ediVersionsRes.requestId !== ediVersionsInvalidToastRequestId.current) {
        ediVersionsInvalidToastRequestId.current = ediVersionsRes.requestId;
        showGlobalErrorToast(t('MSG_FETCH_DATA_TYPES_FAILED', {
          defaultValue: 'Something went wrong while fetching data types.'
        }));
      }
      setVersionLoading(false);
      return;
    }

    setVersionOptions(versionsList);
    const ver = rightTree.tree.templateHeader.FILETYPE_VERSION;
    const first = versionsList[0].key;
    if (!rightTree.tree.templateHeader.FILETYPE_VERSION || versionsList.filter((o) => ver === o.key).length === 0) {
      setVersion(first);
      handleChange('FILETYPE_VERSION', first);
    }
    setVersionLoading(false);
  }, [ediVersionsRes])

  useEffect(() => {
    if (transSetsRes.isError) {
      if (transSetsRes.requestId !== ediTransSetsErrorToastRequestId.current) {
        ediTransSetsErrorToastRequestId.current = transSetsRes.requestId;
        showGlobalErrorToast(t('MSG_FETCH_TRANSACTION_SETS_FAILED', {
          defaultValue: 'Something went wrong while fetching transaction sets.'
        }));
      }
      setTransactionLoading(false);
      setTransactionOptions([]);
      return;
    }

    if (!transSetsRes.isSuccess) return;

    const txn = rightTree.tree.templateHeader.TXN_SET_TYPE;
    const sets = transSetsRes.data?.TRANS_SETS;
    if (!Array.isArray(sets) || sets.length === 0) {
      if (transSetsRes.requestId !== ediTransSetsInvalidToastRequestId.current) {
        ediTransSetsInvalidToastRequestId.current = transSetsRes.requestId;
        showGlobalErrorToast(t('MSG_FETCH_TRANSACTION_SETS_FAILED', {
          defaultValue: 'Something went wrong while fetching transaction sets.'
        }));
      }
      setTransactionLoading(false);
      setTransactionOptions([]);
      return;
    }

    const txnVal = sets.filter((o) => txn === o.templateId).length > 0 ? txn : sets[0].templateId;
    setTransactionOptions(getTransSets());
    setTransaction(txnVal);
    handleChange('TXN_SET_TYPE', txnVal);
    setTransactionLoading(false);
  }, [transSetsRes])


  return (
    <>
      <>
        <div className="flex width-100">
          <FormControl size="small" variant="outlined" className="width-100"> 
            <InputLabel id="versionDropdownLabel">{t('LBL_DATATYPE_VERSION')}</InputLabel>
            <Select
              size="small"
              variant="outlined"
              className="width-100"
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: 250
                  }
                }
              }}
              labelId="versionDropdownLabel"
              value={versionOptions.filter(o => o.text === version).length ? version : ""}
              onChange={(e) => {
                setVersion(e.target.value);
                setTransactionLoading(true); 
                handleChange('FILETYPE_VERSION', e.target.value);
              }}
              label={t('LBL_DATATYPE_VERSION')}
            >
              {versionOptions.map(o => 
                <MenuItem key={'ver-'+o.value} value={o.value}>{o.text}</MenuItem>
              )}
            </Select>
          </FormControl>

          {
            !versionWarningIcon ? '' :
            <LightTooltip title={t('BOX_VER_CHANGE_WARNING')} placement="right-start">
              <Button 
                size='small'
                sx={{
                  width: '30px !important', 
                  padding: '4px 8px !important', 
                  minWidth: '30px !important'
                }}
                onClick={() => setVersionWarningIcon(false)}
                onMouseEnter={() => setVersionWarningIcon(<VisibilityOffIcon sx={{fontSize: '26px', color: '#c4c4c4'}}/>)}
                onMouseLeave={() => setVersionWarningIcon(<ReportProblemRoundedIcon sx={{fontSize: '26px', color: '#c4c4c4'}}/>)}
              >
                {versionWarningIcon || ''}
              </Button>
            </LightTooltip>
          }
        </div>
        <div className="flex width-100">
          <FormControl size="small" variant="outlined" className="width-100">
            <InputLabel id="tranSetDropdownLabel">{t('LBL_TRANS_SET')}</InputLabel>
            <Select
              disabled={transactionLoading}
              className="width-100"
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: 250
                  }
                }
              }}
              labelId="tranSetDropdownLabel"
              value={transactionOptions.filter(o => o.text === transaction).length ? transaction : ""}
              onChange={(e) => {
                setTransaction(e.target.value);
                handleChange('TXN_SET_TYPE', e.target.value)
              }}
              label={t('LBL_TRANS_SET')}
            >
              {transactionOptions.map(o => 
                <MenuItem key={'ver-'+o.value} value={o.value}>{o.text}</MenuItem>
              )}
            </Select>
          </FormControl>

          {
            !transactionWarningIcon ? '' :
            <LightTooltip title={t('BOX_TXN_CHANGE_WARNING')} placement="right-start">
              <Button 
                size='small'
                sx={{
                  width: '30px !important', 
                  padding: '4px 8px !important', 
                  minWidth: '30px !important'
                }}
                onClick={() => setTransactionWarningIcon(false)}
                onMouseEnter={() => setTransactionWarningIcon(<VisibilityOffIcon sx={{fontSize: '26px', color: '#c4c4c4'}}/>)}
                onMouseLeave={() => setTransactionWarningIcon(<ReportProblemRoundedIcon sx={{fontSize: '26px', color: '#c4c4c4'}}/>)}
              >
                {transactionWarningIcon || ''}
              </Button>
            </LightTooltip>
          }
        </div> 
      </>

      <>
        <DelimiterInput
          default
          label={t('LBL_REC_DELIM_')}
          value={recordDelimiter}
          name={'LINEDELIMITER'}
        />
        <DelimiterInput
          default
          label={t('LBL_FLD_DELIM')}
          value={fieldDelimiter}
          name={'DELIMITER'}
        />
        <DelimiterInput
          default
          label={t('LBL_COMP_DELIM')}
          value={componentSeparator}
          name={'GROUP_SEPARATOR'}
        />
        {
          //hideRepetitionSeparator ? '' : 
          ['4010', '4010A1'].includes(version) ? '' :
          <DelimiterInput
            default
            label={t('LBL_REPEAT_DELIM')}
            value={repetitionSeparator}
            name={'REPETITION_DELIMITER'}
          />
        }
      </>

      {parentScreen === 'inbound' && <>
        <div className="flex flex-wrap">
          <TPCheckbox 
            label={t('BTN_STRIP_QUOTES')}
            checked={stripQuotes}
            handleCheck={(checked) => {
              setStripQuotes(checked);
              handleChange('UNCSV', checked);
            }}
          />
          <TPCheckbox 
            label={t('BTN_TRIM_DATA')}
            checked={trimData}
            handleCheck={(checked) => {
              setTrimData(checked);
              handleChange('TRIMDATA', checked);
            }}
          />
          <TPCheckbox 
            label={t('BTN_URLDECODE_DATA')}
            checked={decodeData}
            handleCheck={(checked) => {
              setDecodeData(checked);
              handleChange('URLDECODE', checked);
            }}
          />
        </div>
      <GroupByName />
      </>}
      {parentScreen === 'outbound' && <OverrideInboundDelimeter/>}
    </>
  )
}

const LightTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} style={{marginLeft: "4px"}}/>
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: 'rgba(0, 0, 0, 0.6)',
    boxShadow: theme.shadows[1],
    fontSize: 12,
  },
}));

export default EDIForm;